import React, { useCallback, useEffect, useRef, useState } from 'react';

interface TypingParagrapheProps {
  text: string;
  time: number;
  className: string;
  onIndexChange?: (currentIndex: number) => void;
  onComplete?: () => void;
}

const TypingParagraphe: React.FC<TypingParagrapheProps> = ({
  text,
  time,
  className,
  onIndexChange,
  onComplete,
}) => {
  const [displayText, setDisplayText] = useState('');
  const onIndexChangeRef = useRef(onIndexChange);
  const onCompleteRef = useRef(onComplete);
  onIndexChangeRef.current = onIndexChange;
  onCompleteRef.current = onComplete;

  useEffect(() => {
    let currentIndex = 0;
    setDisplayText('');

    const interval = window.setInterval(() => {
      if (currentIndex <= text.length) {
        setDisplayText(text.slice(0, currentIndex));
        onIndexChangeRef.current?.(currentIndex);
        currentIndex += 1;
      } else {
        window.clearInterval(interval);
        onCompleteRef.current?.();
      }
    }, time);

    return () => window.clearInterval(interval);
  }, [text, time]);

  return (
    <p className={className} style={{ minHeight: '1.5em' }}>
      {displayText}
    </p>
  );
};

interface MessageCarouselProps {
  messages: string[];
  delayBetweenMessages: number;
  typingSpeed: number;
  className: string;
}

/** Carrousel : message suivant seulement après fin de frappe + pause. */
const TypingParagraph: React.FC<MessageCarouselProps> = ({
  messages,
  delayBetweenMessages,
  typingSpeed,
  className,
}) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [waitingNext, setWaitingNext] = useState(false);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const handleComplete = useCallback(() => {
    if (messagesRef.current.length <= 1) return;
    setWaitingNext(true);
  }, []);

  useEffect(() => {
    if (!waitingNext) return;

    const timer = window.setTimeout(() => {
      setWaitingNext(false);
      setCurrentMessageIndex((prev) => (prev + 1) % messagesRef.current.length);
    }, delayBetweenMessages);

    return () => window.clearTimeout(timer);
  }, [waitingNext, delayBetweenMessages]);

  if (messages.length === 0) return null;

  const currentText = messages[currentMessageIndex % messages.length] ?? '';

  return (
    <TypingParagraphe
      key={currentMessageIndex}
      text={currentText}
      time={typingSpeed}
      className={className}
      onComplete={handleComplete}
    />
  );
};

export { TypingParagraphe, TypingParagraph };
